import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UserService } from '../../../services/user.service';
import { CustomToastrService } from '../../../../../services/toastr.service';

// Interface for change password request
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html'
})
export class ChangePasswordComponent implements OnInit {
  changePasswordForm!: FormGroup;
  loading = false;
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ChangePasswordComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { userId: string },
    private userService: UserService,
    private customToastrService: CustomToastrService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.initializeForm();
  }

  initializeForm(): void {
    this.changePasswordForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(14),
        this.passwordStrengthValidator.bind(this)
      ]],
      confirmPassword: ['', [Validators.required]]
    }, {
      validators: this.passwordMatchValidator.bind(this)
    });

    // Add value change listeners to trigger password match validation
    this.changePasswordForm.get('newPassword')?.valueChanges.subscribe(() => {
      this.changePasswordForm.get('confirmPassword')?.updateValueAndValidity();
    });

    this.changePasswordForm.get('confirmPassword')?.valueChanges.subscribe(() => {
      // Trigger form validation to check password match
      this.changePasswordForm.updateValueAndValidity();
    });
  }

  // Custom validator for password strength
  passwordStrengthValidator(control: AbstractControl): { [key: string]: any } | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumeric = /[0-9]/.test(value);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);

    const passwordValid = hasUpperCase && hasLowerCase && hasNumeric && hasSpecialChar;

    return !passwordValid ? { 'passwordStrength': true } : null;
  }

  // Custom validator to check if passwords match
  passwordMatchValidator(form: AbstractControl): { [key: string]: any } | null {
    const newPassword = form.get('newPassword');
    const confirmPassword = form.get('confirmPassword');

    if (!newPassword || !confirmPassword) {
      return null;
    }

    return newPassword.value !== confirmPassword.value ? { 'passwordMismatch': true } : null;
  }

  toggleCurrentPasswordVisibility(): void {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onSubmit(): void {
    if (this.changePasswordForm.valid) {
      this.loading = true;
      const formValue = this.changePasswordForm.value;

      const changePasswordRequest: ChangePasswordRequest = {
        currentPassword: formValue.currentPassword,
        newPassword: formValue.newPassword
      };

      this.userService.changePassword(this.data.userId, changePasswordRequest).subscribe({
        next: () => {
          this.loading = false;
          this.customToastrService.success('Password changed successfully! You will be logged out.', 'Success');
          this.dialogRef.close(true);
          
          // Clear session and logout after password change
          setTimeout(() => {
            localStorage.clear();
            this.router.navigate(['/login']);
          }, 2000); // Wait 2 seconds to show the success message
        },
        error: (error) => {
          this.loading = false;
          console.error('Error changing password:', error);
          
          // Handle specific error messages from API
          let errorMessage = 'Error changing password';
          if (error.error?.Message) {
            errorMessage = error.error.Message;
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          this.customToastrService.error(errorMessage, 'Error');
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.changePasswordForm.get(fieldName);
    const fieldInvalid = !!(field && field.invalid && (field.dirty || field.touched));
    
    // Check for form-level validation errors (like password mismatch)
    if (fieldName === 'confirmPassword') {
      const formInvalid = !!(this.changePasswordForm.errors?.['passwordMismatch'] && 
                            (field?.dirty || field?.touched));
      return fieldInvalid || formInvalid;
    }
    
    return fieldInvalid;
  }

  getFieldError(fieldName: string): string {
    const field = this.changePasswordForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        return `${this.getFieldDisplayName(fieldName)} is required`;
      }
      if (field.errors['minlength']) {
        return `${this.getFieldDisplayName(fieldName)} must be at least ${field.errors['minlength'].requiredLength} characters long`;
      }
      if (field.errors['passwordStrength']) {
        return 'Password must contain uppercase, lowercase, number, and special character';
      }
    }

    // Check form-level errors
    if (fieldName === 'confirmPassword' && this.changePasswordForm.errors?.['passwordMismatch']) {
      return 'New Password and Confirm Password must be the same.';
    }

    return '';
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: { [key: string]: string } = {
      'currentPassword': 'Current Password',
      'newPassword': 'New Password',
      'confirmPassword': 'Confirm Password'
    };
    return displayNames[fieldName] || fieldName;
  }

  private markFormGroupTouched(): void {
    Object.keys(this.changePasswordForm.controls).forEach(key => {
      const control = this.changePasswordForm.get(key);
      control?.markAsTouched();
    });
  }
}